import { create } from 'zustand';

const STORAGE_KEY = 'spx_bookmap_settings_v1';

export const DEFAULT_VOLUME_DOT_MIN_VOLUME = 10000;
export const DEFAULT_GEX_DELTA_FLOW_MIN_VOLUME = DEFAULT_VOLUME_DOT_MIN_VOLUME;
export const DEFAULT_ES_SYNTHETIC_BASIS = 0;
export const DEFAULT_HEAT_LAYER = 'gamma';
export const VOLUME_DOT_THRESHOLD_OPTIONS = [1000, 5000, 10000, 15000, 20000, 25000, 30000, 40000, 50000];
const HEAT_LAYERS = new Set(['gamma', 'delta', 'volume']);

function normalizeThreshold(value) {
  if (value === null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_VOLUME_DOT_MIN_VOLUME;
}

function normalizeBasis(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : DEFAULT_ES_SYNTHETIC_BASIS;
}

function normalizeHeatLayer(value) {
  return HEAT_LAYERS.has(value) ? value : DEFAULT_HEAT_LAYER;
}

function loadSettings() {
  if (typeof localStorage === 'undefined') {
    return {
      volumeDotMinVolume: DEFAULT_VOLUME_DOT_MIN_VOLUME,
      gexDeltaFlowMinVolume: DEFAULT_GEX_DELTA_FLOW_MIN_VOLUME,
      esSyntheticBasis: DEFAULT_ES_SYNTHETIC_BASIS,
      heatLayer: DEFAULT_HEAT_LAYER,
    };
  }

  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return {
      volumeDotMinVolume: normalizeThreshold(parsed.volumeDotMinVolume),
      gexDeltaFlowMinVolume: normalizeThreshold(parsed.gexDeltaFlowMinVolume),
      esSyntheticBasis: normalizeBasis(parsed.esSyntheticBasis),
      heatLayer: normalizeHeatLayer(parsed.heatLayer),
    };
  } catch {
    return {
      volumeDotMinVolume: DEFAULT_VOLUME_DOT_MIN_VOLUME,
      gexDeltaFlowMinVolume: DEFAULT_GEX_DELTA_FLOW_MIN_VOLUME,
      esSyntheticBasis: DEFAULT_ES_SYNTHETIC_BASIS,
      heatLayer: DEFAULT_HEAT_LAYER,
    };
  }
}

function saveSettings(settings) {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export function formatVolumeThreshold(value) {
  const normalized = normalizeThreshold(value);
  if (normalized >= 1000000) return `${normalized / 1000000}M`;
  if (normalized >= 1000) return `${normalized / 1000}k`;
  return String(normalized);
}

const initialSettings = loadSettings();

export const useBookmapSettingsStore = create((set) => ({
  volumeDotMinVolume: initialSettings.volumeDotMinVolume,
  gexDeltaFlowMinVolume: initialSettings.gexDeltaFlowMinVolume,
  esSyntheticBasis: initialSettings.esSyntheticBasis,
  heatLayer: initialSettings.heatLayer,
  setVolumeDotMinVolume: (value) => set((state) => {
    const next = {
      ...state,
      volumeDotMinVolume: normalizeThreshold(value),
    };
    saveSettings({
      volumeDotMinVolume: next.volumeDotMinVolume,
      gexDeltaFlowMinVolume: state.gexDeltaFlowMinVolume,
      esSyntheticBasis: state.esSyntheticBasis,
      heatLayer: state.heatLayer,
    });
    return next;
  }),
  setGexDeltaFlowMinVolume: (value) => set((state) => {
    const next = {
      ...state,
      gexDeltaFlowMinVolume: normalizeThreshold(value),
    };
    saveSettings({
      volumeDotMinVolume: state.volumeDotMinVolume,
      gexDeltaFlowMinVolume: next.gexDeltaFlowMinVolume,
      esSyntheticBasis: state.esSyntheticBasis,
      heatLayer: state.heatLayer,
    });
    return next;
  }),
  setEsSyntheticBasis: (value) => set((state) => {
    const next = {
      ...state,
      esSyntheticBasis: normalizeBasis(value),
    };
    saveSettings({
      volumeDotMinVolume: state.volumeDotMinVolume,
      gexDeltaFlowMinVolume: state.gexDeltaFlowMinVolume,
      esSyntheticBasis: next.esSyntheticBasis,
      heatLayer: state.heatLayer,
    });
    return next;
  }),
  setHeatLayer: (value) => set((state) => {
    const next = {
      ...state,
      heatLayer: normalizeHeatLayer(value),
    };
    saveSettings({
      volumeDotMinVolume: state.volumeDotMinVolume,
      gexDeltaFlowMinVolume: state.gexDeltaFlowMinVolume,
      esSyntheticBasis: state.esSyntheticBasis,
      heatLayer: next.heatLayer,
    });
    return next;
  }),
}));